/*
 * WorkflowSettingsPanel — right sidebar for workflow-LEVEL settings, opened from
 * the editor toolbar (Cluster 5 Slice 2 / A8 pt 5). Patterned on NodeConfigPanel
 * (inline right panel + onClose).
 *
 * Surfaces (a) the per-workflow Pathway D toggle and (b) the Change-2 (2026-06-14)
 * department-access multi-select: which departments a workflow is assigned to. A
 * plain member sees/uses a workflow only if they hold the workflow-use grant in
 * ≥1 of its departments; zero departments = admin/power-only (R2). Both are
 * editable by BUILDERS (admin||power_user) — R-panel / R6 (who assigns = who
 * builds). The designer route is builder-guarded, so a plain member never opens
 * this; the `disabled={!canEdit}` guards are defensive.
 */

import React, { useEffect, useState } from 'react';
import { captureAPI } from '../../services/api';
import './WorkflowSettingsPanel.css';

const WorkflowSettingsPanel = ({ workflow, canEdit, saving, onChangePathwayD, onChangeDepartments, onClose }) => {
  const [allDepts, setAllDepts] = useState([]);
  const [selected, setSelected] = useState(() => new Set(workflow?.department_keys || []));
  const [deptSaving, setDeptSaving] = useState(false);

  // Load the tenant's departments (active) for the picker.
  useEffect(() => {
    let active = true;
    captureAPI.getDepartments()
      .then((res) => { if (active) setAllDepts((res?.departments || []).filter((d) => d.is_active !== false)); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Re-sync the selection when the loaded workflow's set changes.
  useEffect(() => {
    setSelected(new Set(workflow?.department_keys || []));
  }, [workflow?.id, workflow?.department_keys]);

  if (!workflow) return null;

  const pathwayDEnabled = workflow.pathway_d_enabled ?? true;
  const current = new Set(workflow.department_keys || []);
  const dirty = current.size !== selected.size || [...selected].some((k) => !current.has(k));

  const toggleDept = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveDepts = async () => {
    setDeptSaving(true);
    try {
      await onChangeDepartments(Array.from(selected));
    } finally {
      setDeptSaving(false);
    }
  };

  return (
    <div className="wsp-panel">
      <div className="wsp-header">
        <span className="wsp-header-icon">⚙️</span>
        <div className="wsp-header-info">
          <div className="wsp-header-label">Workflow Settings</div>
          <div className="wsp-header-type">{workflow.name || 'Untitled Workflow'}</div>
        </div>
        <button className="wsp-close" onClick={onClose} title="Close">×</button>
      </div>

      <div className="wsp-body">
        <div className="wsp-section-title">AI behavior</div>
        <div className="wsp-field">
          <label className="wsp-toggle-label">
            <input
              type="checkbox"
              className="wsp-toggle-input"
              checked={pathwayDEnabled}
              onChange={(e) => onChangePathwayD(e.target.checked)}
              disabled={!canEdit || saving}
            />
            <span className="wsp-toggle-text">Claude AI fallback (Pathway D)</span>
          </label>
          <p className="wsp-description">
            When a document matches no vendor profile and no template, Claude AI extracts it
            (Pathway D). With this off, such documents are routed to human review instead.
          </p>
        </div>

        <div className="wsp-section-title" style={{ marginTop: 18 }}>Department access</div>
        <div className="wsp-field">
          <p className="wsp-description" style={{ marginTop: 0 }}>
            Members who can work in a selected department can use this workflow. With none selected,
            only admins and power users can see it.
          </p>
          <div className="wsp-dept-list">
            {allDepts.length === 0 && <p className="wsp-description">No departments.</p>}
            {allDepts.map((d) => (
              <label key={d.key} className="wsp-toggle-label">
                <input
                  type="checkbox"
                  className="wsp-toggle-input"
                  checked={selected.has(d.key)}
                  onChange={() => toggleDept(d.key)}
                  disabled={!canEdit || deptSaving}
                />
                <span className="wsp-toggle-text">{d.label || d.key}</span>
              </label>
            ))}
          </div>
          {canEdit && (
            <button
              className="wsp-save-btn"
              onClick={saveDepts}
              disabled={!dirty || deptSaving}
            >
              {deptSaving ? 'Saving…' : dirty ? 'Save departments' : 'Saved'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowSettingsPanel;
